import { useState, useEffect } from 'react';
import { FaTrash, FaImage, FaSpinner, FaPlus, FaVolumeUp, FaLanguage } from 'react-icons/fa';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { translateBatchToText } from '../../../utilities/extensions/translateToText';
import Constants from '../../../utilities/constants/Constants';
import { generateBatchSpeechAudio, LanguageCodes, VoiceGenders } from '../../../utilities/extensions/textToSpeech';
import { db } from '../../../firebase';
import { generateActivityStoragePath } from '../../../utilities/storagePathHelper';

const WordPracticeActivity = ({ initialContent, onContentChange, chapterId, grade, selectedChapter }) => {
  const [content, setContent] = useState({
    words: [{ 
      languageWord: '', 
      englishWord: '',
      confusingLetters: '',
      languageAudio: null,
      englishAudio: null,
      image: null
    }]
  });
  const [uploadingFiles, setUploadingFiles] = useState({});
  const [generatingAudio, setGeneratingAudio] = useState(false);
  const [translating, setTranslating] = useState(false);

  // Build storage path using the helper
  const buildStoragePath = ({
    wordId,
    fileName
  }) => {
    const language = selectedChapter?.subject?.toLowerCase() || 'language';
    const gradeStr = `grade${grade}`;
    
    return generateActivityStoragePath({
      language,
      grade: gradeStr,
      chapterId,
      activityName: 'word_practice',
      fileName,
      itemId: wordId
    });
  };

  useEffect(() => {
    if (initialContent) {
      try {
        const parsed = JSON.parse(initialContent);
        setContent(parsed);
      } catch {
        // Keep default content if parsing fails
      }
    }
  }, [initialContent]);

  useEffect(() => {
    onContentChange(content);
  }, [content, onContentChange]);

  const addWord = () => {
    const newWords = [...content.words, { 
      languageWord: '', 
      englishWord: '',
      confusingLetters: '',
      languageAudio: null,
      englishAudio: null,
      image: null
    }];
    setContent(prev => ({ ...prev, words: newWords }));
  };

  const removeWord = async (index) => {
    if (content.words.length > 1) {
      const wordToDelete = content.words[index];
      const storage = getStorage();
      
      // Delete all associated files from Firebase Storage
      const fileTypes = ['languageAudio', 'englishAudio', 'image'];
      
      for (const fileType of fileTypes) {
        if (wordToDelete[fileType]) {
          try {
            const fileRef = ref(storage, wordToDelete[fileType]);
            await deleteObject(fileRef);
          } catch (error) {

        // Ignore errors
          }
        }
      }
      
      const newWords = content.words.filter((_, i) => i !== index);
      setContent(prev => ({ ...prev, words: newWords }));
    }
  };

  const updateWord = (index, field, value) => {
    const newWords = [...content.words];
    newWords[index] = { ...newWords[index], [field]: value };
    setContent(prev => ({ ...prev, words: newWords }));
  };

  const handleFileUpload = async (index, fileType, file) => {
    if (!file) return;

    const uploadKey = `${index}-${fileType}`;
    setUploadingFiles(prev => ({ ...prev, [uploadKey]: true }));

    try {
      const storage = getStorage();
      const wordId = `word${index + 1}`;
      
      const storagePath = buildStoragePath({
        wordId,
        fileName: file.name
      });

      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef); // eslint-disable-line no-unused-vars

      // Delete old file if exists
      const oldPath = content.words[index][fileType];
      if (oldPath) {
        try {
          const oldRef = ref(storage, oldPath);
          await deleteObject(oldRef);
        } catch (error) {

        // Ignore errors
        }
      }

      updateWord(index, fileType, storagePath);
    } catch (error) {

      alert('Failed to upload file. Please try again.');
    } finally {
      setUploadingFiles(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const generateAllAudio = async () => {
    setGeneratingAudio(true);
    try {
      const storage = getStorage();
      
      // Collect all texts that need audio generation
      const languageTexts = content.words.map(w => w.languageWord).filter(Boolean);
      const englishTexts = content.words.map(w => w.englishWord).filter(Boolean);

      if (languageTexts.length === 0 && englishTexts.length === 0) {
        alert('No words found to generate audio. Please add words first.');
        return;
      }

      let audioGenerated = false;
      const updatedWords = [...content.words];

      // Generate audio for all language words
      if (languageTexts.length > 0) {
        const languageRequests = languageTexts.map(text => ({
          text,
          languageCode: LanguageCodes.KANNADA,
          ssmlGender: VoiceGenders.FEMALE
        }));
        const languageAudioUrls = await generateBatchSpeechAudio(languageRequests, db, storage);

        // Store language audio URLs
        let languageIndex = 0;
        for (let i = 0; i < updatedWords.length; i++) {
          if (updatedWords[i].languageWord && updatedWords[i].languageWord.trim()) {
            if (languageIndex < languageAudioUrls.length) {
              const audioUrl = languageAudioUrls[languageIndex];
              if (audioUrl && !audioUrl.startsWith('[TTS Error:')) {
                updatedWords[i].languageAudio = audioUrl;
                audioGenerated = true;
              }
            }
            languageIndex++;
          }
        }
      }

      // Generate audio for all English words
      if (englishTexts.length > 0) {
        const englishRequests = englishTexts.map(text => ({
          text,
          languageCode: LanguageCodes.ENGLISH,
          ssmlGender: VoiceGenders.FEMALE
        }));
        const englishAudioUrls = await generateBatchSpeechAudio(englishRequests, db, storage);

        // Store English audio URLs
        let englishIndex = 0;
        for (let i = 0; i < updatedWords.length; i++) {
          if (updatedWords[i].englishWord && updatedWords[i].englishWord.trim()) {
            if (englishIndex < englishAudioUrls.length) {
              const audioUrl = englishAudioUrls[englishIndex];
              if (audioUrl && !audioUrl.startsWith('[TTS Error:')) {
                updatedWords[i].englishAudio = audioUrl;
                audioGenerated = true;
              }
            }
            englishIndex++;
          }
        }
      }

      // Update state once with all changes
      setContent({ ...content, words: updatedWords });

      if (audioGenerated) {
        alert('Audio generated successfully for all words!');
      } else {
        alert('Failed to generate audio. Please check the words and try again.');
      }
    } catch (error) {

      alert('Failed to generate audio: ' + error.message);
    } finally {
      setGeneratingAudio(false);
    }
  };

  const playAudio = async (audioPath) => {
    if (!audioPath) {
      alert('No audio file available');
      return;
    }

    try {
      const storage = getStorage();
      const audioRef = ref(storage, audioPath);
      const url = await getDownloadURL(audioRef);
      const audio = new Audio(url);
      audio.play();
    } catch (error) {

      alert('Failed to play audio');
    }
  };

  const translateAllWords = async () => {
    setTranslating(true);

    try {
      const storage = getStorage();
      
      // Extract language words that don't have English translations
      const wordsToTranslate = content.words
        .map((word, index) => ({ text: word.languageWord, index }))
        .filter(item => item.text && item.text.trim() && !content.words[item.index].englishWord);

      if (wordsToTranslate.length === 0) {
        alert('All words already have English translations or no language words to translate.');
        return;
      }

      // Translate the language words (Kannada to English)
      const textsToTranslate = wordsToTranslate.map(item => item.text);
      const translatedTexts = await translateBatchToText(
        textsToTranslate, 
        db, 
        5, 
        2000, 
        Constants.LANGUAGES.KANNADA, 
        Constants.LANGUAGES.ENGLISH
      );
      
      // Update words with English translations
      const updatedWords = [...content.words];
      const successfulTranslations = [];
      
      wordsToTranslate.forEach((item, translationIndex) => {
        const translatedText = translatedTexts[translationIndex];
        if (translatedText && !translatedText.startsWith('[Translation Error:')) {
          updatedWords[item.index].englishWord = translatedText;
          successfulTranslations.push({
            text: translatedText,
            index: item.index
          });
        }
      });
      
      // Generate audio for the translated English words
      if (successfulTranslations.length > 0) {
        const englishRequests = successfulTranslations.map(t => ({
          text: t.text,
          languageCode: LanguageCodes.ENGLISH,
          ssmlGender: VoiceGenders.FEMALE
        }));
        const englishAudioUrls = await generateBatchSpeechAudio(englishRequests, db, storage);

        // Store English audio URLs
        for (let i = 0; i < successfulTranslations.length; i++) {
          const translation = successfulTranslations[i];
          const audioUrl = englishAudioUrls[i];
          
          if (audioUrl && !audioUrl.startsWith('[TTS Error:')) {
            updatedWords[translation.index].englishAudio = audioUrl;
          }
        }
      }
      
      setContent({ ...content, words: updatedWords });
      alert('Translation and audio generation completed successfully!');
    } catch (error) {

      alert('Failed to translate words: ' + error.message);
    } finally {
      setTranslating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Words Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">Word Practice</h3>
            <p className="text-sm text-gray-600 mt-1">{content.words.length} word{content.words.length !== 1 ? 's' : ''} configured</p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={translateAllWords}
              disabled={translating || generatingAudio}
              className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium shadow-sm disabled:bg-purple-400 disabled:cursor-not-allowed"
            >
              {translating ? (
                <>
                  <FaSpinner className="w-4 h-4 mr-2 animate-spin" />
                  Translating...
                </>
              ) : (
                <>
                  <FaLanguage className="w-4 h-4 mr-2" />
                  Translate to English
                </>
              )}
            </button>
            <button
              type="button"
              onClick={generateAllAudio}
              disabled={generatingAudio || translating}
              className="flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium shadow-sm disabled:bg-emerald-400 disabled:cursor-not-allowed"
            >
              {generatingAudio ? (
                <>
                  <FaSpinner className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <FaVolumeUp className="w-4 h-4 mr-2" />
                  Generate Audio
                </>
              )}
            </button>
            <button
              type="button"
              onClick={addWord}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm"
            >
              <FaPlus className="w-4 h-4 mr-2" />
              Add Word
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {content.words.map((word, index) => (
            <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center gap-4">
                {/* Index */}
                <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0">
                  {index + 1}
                </div>
                
                {/* Text Fields */}
                <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                  <input
                    type="text"
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors text-sm"
                    placeholder="language word *"
                    value={word.languageWord || ''}
                    onChange={(e) => updateWord(index, 'languageWord', e.target.value)}
                  />
                  <input
                    type="text"
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors text-sm"
                    placeholder="English word *"
                    value={word.englishWord || ''}
                    onChange={(e) => updateWord(index, 'englishWord', e.target.value)}
                  />
                  <input
                    type="text"
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors text-sm"
                    placeholder="Confusing letters (space-separated) *"
                    value={word.confusingLetters || ''}
                    onChange={(e) => updateWord(index, 'confusingLetters', e.target.value)}
                  />
                </div>
                
                {/* Audio Files - Compact */}
                <div className="flex items-center gap-2">
                  {/* language Audio */}
                  <div className="flex items-center gap-1">
                    <input
                      type="file"
                      accept="audio/*"
                      className="hidden"
                      id={`ka-audio-${index}`}
                      onChange={(e) => handleFileUpload(index, 'languageAudio', e.target.files[0])}
                      disabled={uploadingFiles[`${index}-languageAudio`]}
                    />
                    <label
                      htmlFor={`ka-audio-${index}`}
                      className="cursor-pointer px-2 py-1 bg-purple-50 text-purple-700 rounded text-xs hover:bg-purple-100 transition-colors"
                      title="language Audio"
                    >
                      🎵 KA
                    </label>
                    {uploadingFiles[`${index}-languageAudio`] && <FaSpinner className="w-3 h-3 animate-spin text-purple-600" />}
                    {word.languageAudio && !uploadingFiles[`${index}-languageAudio`] && (
                      <button
                        type="button"
                        className="p-1 text-green-600 hover:bg-green-100 rounded"
                        onClick={() => playAudio(word.languageAudio)}
                        title="Play language Audio"
                      >
                        <FaVolumeUp className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  
                  {/* English Audio */}
                  <div className="flex items-center gap-1">
                    <input
                      type="file"
                      accept="audio/*"
                      className="hidden"
                      id={`ea-audio-${index}`}
                      onChange={(e) => handleFileUpload(index, 'englishAudio', e.target.files[0])}
                      disabled={uploadingFiles[`${index}-englishAudio`]}
                    />
                    <label
                      htmlFor={`ea-audio-${index}`}
                      className="cursor-pointer px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs hover:bg-blue-100 transition-colors"
                      title="English Audio"
                    >
                      🎵 EA
                    </label>
                    {uploadingFiles[`${index}-englishAudio`] && <FaSpinner className="w-3 h-3 animate-spin text-blue-600" />}
                    {word.englishAudio && !uploadingFiles[`${index}-englishAudio`] && (
                      <button
                        type="button"
                        className="p-1 text-green-600 hover:bg-green-100 rounded"
                        onClick={() => playAudio(word.englishAudio)}
                        title="Play English Audio"
                      >
                        <FaVolumeUp className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Image - Compact */}
                <div className="flex items-center gap-1">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    id={`image-${index}`}
                    onChange={(e) => handleFileUpload(index, 'image', e.target.files[0])}
                    disabled={uploadingFiles[`${index}-image`]}
                  />
                  <label
                    htmlFor={`image-${index}`}
                    className="cursor-pointer px-2 py-1 bg-pink-50 text-pink-700 rounded text-xs hover:bg-pink-100 transition-colors"
                    title="Upload Image"
                  >
                    🖼️ IMG
                  </label>
                  {uploadingFiles[`${index}-image`] && <FaSpinner className="w-3 h-3 animate-spin text-pink-600" />}
                  {word.image && !uploadingFiles[`${index}-image`] && (
                    <button
                      type="button"
                      className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                      onClick={async () => {
                        try {
                          const storage = getStorage();
                          const imageRef = ref(storage, word.image);
                          const downloadURL = await getDownloadURL(imageRef);
                          window.open(downloadURL, '_blank');
                        } catch (error) {

        // Ignore errors
                        }
                      }}
                      title="View Image"
                    >
                      <FaImage className="w-3 h-3" />
                    </button>
                  )}
                </div>
                
                {/* Delete Button */}
                {content.words.length > 1 && (
                  <button
                    type="button"
                    className="p-2 text-red-600 hover:bg-red-100 rounded transition-colors"
                    onClick={() => removeWord(index)}
                    title="Remove Word"
                  >
                    <FaTrash className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Validation function
export const validateWordPracticeContent = (content) => {
  const errors = [];

  if (!content || typeof content !== 'object') {
    errors.push('Content must be an object');
    return errors;
  }

  if (!content.words || !Array.isArray(content.words)) {
    errors.push('Content must have a words array');
    return errors;
  }

  if (content.words.length === 0) {
    errors.push('At least one word is required');
    return errors;
  }

  content.words.forEach((word, index) => {
    if (!word.languageWord || word.languageWord.trim() === '') {
      errors.push(`Word ${index + 1}: language word is required`);
    }
    if (!word.englishWord || word.englishWord.trim() === '') {
      errors.push(`Word ${index + 1}: English word is required`);
    }
    if (!word.confusingLetters || word.confusingLetters.trim() === '') {
      errors.push(`Word ${index + 1}: Confusing letters are required`);
    }
    
    // Validate English word: must start with capital letter
    if (word.englishWord?.trim()) {
      const trimmed = word.englishWord.trim();
      if (!/^[A-Z]/.test(trimmed)) {
        errors.push(`Word ${index + 1}: English word must start with a capital letter`);
      }
    }
  });

  return errors;
};

export default WordPracticeActivity;
